"use client";

import React from "react";
import { UserCheck, Zap, Coins, ShieldCheck, Sparkles, Clock, ArrowRight, Check, X, Target } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const benefits = [{
  id: "1",
  title: "100% Direct Access to Me",
  highlight: "Senior Expertise Only",
  consultantPoint: "You work directly with me, Yassine Essaadi. Every audit, strategy, and code recommendation is crafted and delivered by a senior specialist.",
  agencyPoint: "Pitched by senior directors, but delegated to junior account executives learning on your monthly retainer.",
  iconName: UserCheck
}, {
  id: "2",
  title: "Zero Agency Overhead Costs",
  highlight: "Maximum ROI",
  consultantPoint: "Your budget goes entirely into high-impact SEO work, not funding lavish agency offices, sales reps, or account managers.",
  agencyPoint: "Up to 60% of your retainer pays for agency management overheads and office space rather than actual SEO execution.",
  iconName: Coins
}, {
  id: "3",
  title: "Bespoke & Agile Implementation",
  highlight: "Tailored to Growth",
  consultantPoint: "I build custom strategies tailored to your exact industry and site architecture, pivoting instantly as search engines evolve.",
  agencyPoint: "Locked into rigid, automated monthly deliverables and standardized checklists that ignore strategic opportunities.",
  iconName: Zap
}, {
  id: "4",
  title: "Unfiltered Accountability",
  highlight: "Honest Metrics",
  consultantPoint: "I provide transparent, revenue-focused reporting and direct updates, taking personal ownership of your search visibility.",
  agencyPoint: "Filtered reports managed by middle-tier account managers, often highlighting vanity metrics over actual lead impact.",
  iconName: ShieldCheck
}, {
  id: "5",
  title: "Fast-Tracked Speed to Market",
  highlight: "Rapid Execution",
  consultantPoint: "No bureaucratic red tape or layered approval chains—I collaborate directly with your internal team for fast implementation.",
  agencyPoint: "Slow turnaround times driven by internal ticket systems, account review queues, and multi-layered management approvals.",
  iconName: Clock
}, {
  id: "6",
  title: "Modern & AI-Ready SEO Focus",
  highlight: "Future-Proof",
  consultantPoint: "I actively adapt your organic search strategy to modern AI search engines and Google updates in real time.",
  agencyPoint: "Bound to outdated link building packages or legacy SEO retainer models slow to adapt to modern search habits.",
  iconName: Sparkles
}];
export default function ConsultantBenefits() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: shouldReduceMotion ? {
      opacity: 1,
      y: 0
    } : {
      opacity: 0,
      y: 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="consultantvsagencybenefits" data-nav="light" className="relative py-20 lg:py-28 bg-background text-foreground overflow-hidden">
      <div className="absolute top-1/4 left-0 -z-10 h-96 w-96 rounded-full bg-secondary/40 blur-3xl opacity-60 pointer-events-none" />
      <div className="absolute bottom-10 right-0 -z-10 h-80 w-80 rounded-full bg-secondary/30 blur-3xl opacity-50 pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <Badge variant="secondary" className="px-3 py-1 text-sm font-medium border border-accent/20 text-foreground bg-secondary">
            <Target className="w-3.5 h-3.5 text-accent mr-1.5" />
            Independent Consultant vs Agency
          </Badge>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground font-serif">
            Why Work Directly With Me Instead of an Agency?
          </h2>

          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            When you hire an agency, you often pay for account managers and junior executives. When you hire me, Yassine Essaadi, you get senior-level SEO strategy, complete focus, and transparent execution directly tied to your growth.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {benefits.map((benefit, i) => {
          const IconComponent = benefit.iconName;
          return <motion.div key={benefit.id} variants={itemVariants} data-index={i}>
                <Card className="h-full border border-border shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300 hover:border-primary/40 bg-card flex flex-col justify-between">
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="p-2.5 rounded-lg bg-primary/10 text-primary">
                        <IconComponent className="w-6 h-6" />
                      </div>
                      <Badge className="bg-secondary text-foreground border-accent/30 text-xs px-2.5 py-0.5 font-medium">
                        {benefit.highlight}
                      </Badge>
                    </div>

                    <CardTitle className="text-xl font-bold text-foreground font-serif">
                      {benefit.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="space-y-4 flex-1 flex flex-col justify-between">
                    <div className="p-3.5 rounded-lg bg-secondary/30 border border-accent/20">
                      <div className="flex items-center gap-2 mb-1.5 font-semibold text-sm text-primary">
                        <Check className="w-4 h-4 text-primary shrink-0" />
                        <span>Working With Me:</span>
                      </div>
                      <p className="text-sm text-foreground/90 leading-relaxed">
                        {benefit.consultantPoint}
                      </p>
                    </div>

                    <div className="p-3.5 rounded-lg bg-muted/50 border border-border/60">
                      <div className="flex items-center gap-2 mb-1.5 font-medium text-sm text-muted-foreground">
                        <X className="w-4 h-4 text-destructive shrink-0" />
                        <span>Typical SEO Agency:</span>
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {benefit.agencyPoint}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        <div className="mt-16 bg-card border border-border rounded-2xl p-6 md:p-10 shadow-sm relative overflow-hidden">
          <div className="absolute top-0 right-0 w-2 h-full bg-primary" />
          
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="space-y-3 max-w-2xl text-center lg:text-left">
              <span className="text-xs font-semibold tracking-wider text-accent uppercase">
                Personal Ownership & Commitment
              </span>
              <h3 className="text-2xl md:text-3xl font-bold text-foreground font-serif">
                {"You work directly with me — no middle management."}
              </h3>
              <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                {"I limit the number of clients I accept at any given time to ensure your campaign receives the dedication, strategic depth, and hands-on SEO expertise it deserves."}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-3 w-full lg:w-auto shrink-0">
              <Button asChild size="lg" className="w-full sm:w-auto px-6 font-medium">
                <Link to="/ContactPage">
                  Book a Free SEO Consultation
                  <ArrowRight className="w-4 h-4 ml-1.5" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto px-6">
                <Link to="/CaseStudiesPage">
                  Explore My Case Studies
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}